<script setup lang="ts">
  import { debounce } from "@krainovsd/js-helpers";
  // import { VCheckOutlined } from "@krainovsd/vue-icons";
  import { VCheckBox, VInput } from "@krainovsd/vue-ui";
  import { computed, ref, useTemplateRef, watch } from "vue";
  import { CACHE_NAMESPACE, CACHE_TTL, useCachedResource } from "@/entities/cache";
  import { fetchTrafficProviders } from "@/entities/providers";
  import type { IProvider } from "@/entities/providers";
  import VirtualList from "@/components/atoms/VirtualList.vue";
  import StepLayout from "@/components/molecules/constructor/StepLayout.vue";

  const PROVIDER_ITEM_HEIGHT = 44;
  const SEARCH_DEBOUNCE_MS = 250;

  const selectedProviders = defineModel<Set<number>>({ required: true });
  const emit = defineEmits<{ next: []; back: [] }>();

  // At least one provider must be selected.
  const valid = computed(() => selectedProviders.value.size > 0);

  const layoutRef = ref<{ rootEl: HTMLElement | null } | null>(null);
  defineExpose({
    rootEl: computed(() => layoutRef.value?.rootEl ?? null),
  });

  const { data: providersData } = useCachedResource<IProvider[]>({
    namespace: CACHE_NAMESPACE.TrafficProviders,
    key: "all",
    ttl: CACHE_TTL.Long,
    strategy: "stale-while-revalidate",
    fetcher: () => fetchTrafficProviders(),
  });

  const providerOptions = computed(() => providersData.value ?? []);

  const providerSearch = ref("");
  const debouncedSearch = ref("");
  // const isOpenCustomProvider = ref(false);
  // const customProviderId = ref("");

  const providersListRef = useTemplateRef<{
    scrollToOffset: (offset: number) => void;
  } | null>("providersListRef");

  const applySearch = debounce((value: string) => {
    debouncedSearch.value = value;
    providersListRef.value?.scrollToOffset(0);
  }, SEARCH_DEBOUNCE_MS);

  watch(providerSearch, (value) => applySearch(value));

  const filteredProviders = computed(() => {
    const query = debouncedSearch.value.trim().toLowerCase();
    const matched = query
      ? providerOptions.value.filter(
          (option) =>
            option.name.toLowerCase().includes(query) || String(option.id).includes(query),
        )
      : providerOptions.value;

    // Float selected providers to the top. Array.sort is stable, so the
    // original order is preserved within the selected and unselected groups.
    return [...matched].sort(
      (a, b) =>
        Number(selectedProviders.value.has(b.id)) - Number(selectedProviders.value.has(a.id)),
    );
  });

  function providerKey(provider: IProvider) {
    return provider.id;
  }

  function toggleProvider(key: number) {
    const next = new Set(selectedProviders.value);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    selectedProviders.value = next;
  }

  const allSelected = computed(
    () =>
      providerOptions.value.length > 0 &&
      providerOptions.value.every((option) => selectedProviders.value.has(providerKey(option))),
  );

  function toggleSelectAll() {
    selectedProviders.value = allSelected.value
      ? new Set()
      : new Set(providerOptions.value.map(providerKey));
  }
</script>

<template>
  <StepLayout
    ref="layoutRef"
    :title="$t('page.createReport.step2.title', {}, { default: 'Выберите поставщиков' })"
    :subtitle="
      $t(
        'page.createReport.step2.subtitle',
        {},
        { default: 'Можно выбрать из списка или добавить по ID' },
      )
    "
    back
    next
    :disabled="!valid"
    @back="emit('back')"
    @next="emit('next')"
  >
    <div :class="$style.providersControl">
      <button type="button" :class="$style.selectAll" @click="toggleSelectAll">
        {{
          allSelected
            ? $t("page.createReport.step2.deselectAll", {}, { default: "Снять все" })
            : $t("page.createReport.step2.selectAll", {}, { default: "Выбрать все" })
        }}
      </button>
      <span :class="$style.providersCount">
        {{ $t("page.createReport.step2.selected", {}, { default: "Выбрано" }) }}
        {{ selectedProviders.size }}
        {{ $t("page.createReport.step2.from", {}, { default: "из" }) }}
        {{ filteredProviders.length }}
      </span>
    </div>
    <VInput
      v-model="providerSearch"
      :class="$style.providerSearch"
      shape="round"
      size="large"
      :placeholder="
        $t('page.createReport.step2.searchPlaceholder', {}, { default: 'Поиск по названию или ID' })
      "
    />
    <VirtualList
      ref="providersListRef"
      :class="$style.providersList"
      :items="filteredProviders"
      :item-size="PROVIDER_ITEM_HEIGHT"
    >
      <template #default="{ item }">
        <div :class="$style.providersItem" @click="toggleProvider(providerKey(item))">
          <VCheckBox
            :model-value="selectedProviders.has(providerKey(item))"
            @click.stop
            @update:model-value="toggleProvider(providerKey(item))"
          >
            {{ item.name }}
          </VCheckBox>
        </div>
      </template>
    </VirtualList>
    <!-- <VButton
      v-if="!isOpenCustomProvider"
      type="text"
      size="large"
      @click="isOpenCustomProvider = true"
    >
      {{
        $t("page.createReport.addCustomProvider", {}, { default: "+ Добавить поставщика по ID" })
      }}
    </VButton> -->
    <!-- <div v-if="isOpenCustomProvider" :class="$style.customProvider">
      <VInput
        v-model="customProviderId"
        :class="$style.addCustomProviderInput"
        shape="round"
        size="large"
        :placeholder="
          $t(
            'page.createReport.step2.searchPlaceholder',
            {},
            { default: 'Поиск по названию или ID' },
          )
        "
      />
      <VButton size="large">
        <template #icon><VCheckOutlined /></template>
      </VButton>
    </div> -->
  </StepLayout>
</template>

<style lang="scss" module>
  .providersControl {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .providersCount {
    color: var(--muted-foreground);
    font-size: 0.875rem;
  }

  .providerSearch {
    background-color: var(--ksd-bg-color) !important;
  }

  .selectAll {
    padding: 0;

    background: transparent;
    border: none;
    color: var(--muted-foreground);
    cursor: pointer;

    font-family: inherit;
    font-size: 0.875rem;
    font-weight: 500;
  }

  .providersList {
    height: 372px;

    border: 1px solid var(--border);
    border-radius: 0.5rem;
  }

  .providersItem {
    $height: 44px;
    width: 100%;
    height: $height;
    min-height: $height;

    padding-left: 0.875rem;
    padding-right: 0.875rem;

    display: flex;
    align-items: center;
    gap: 0.75rem;

    border-bottom: 1px solid var(--border);
  }

  .customProvider {
    display: flex;
    gap: 8px;
  }

  .addCustomProviderInput {
    width: 100%;
  }
</style>
